<template>
  <Breadcrumb :home="home" :model="breadcrumbItems">
    <template #item="{ item, props }">
      <router-link v-if="item.route" v-slot="{ href, navigate }" :to="item.route" custom>
        <a :href="href" v-bind="props.action" @click="navigate">
          <span :class="[item.icon, 'text-color']" />
          <span class="text-primary font-semibold">{{ item.label }}</span>
        </a>
      </router-link>
      <a v-else :href="item.url" :target="item.target" v-bind="props.action">
        <span class="text-surface-700 dark:text-surface-0">{{ item.label }}</span>
      </a>
    </template>
  </Breadcrumb>
  <div class="md:px-4 space-y-6">
    <div v-if="loading" class="flex justify-center items-center h-64">
      <i class="pi pi-spin pi-spinner text-4xl text-primary"></i>
    </div>
    <div v-else-if="!currentOrganization" class="flex justify-center items-center h-64">
      <p class="text-gray-500">Organization not found</p>
    </div>
    <div
      v-else
      ref="swipeTarget"
      class="h-full w-full"
      style="touch-action: pan-y; user-select: none"
    >
      <!-- Organization Details Card -->
      <Card class="mb-4">
        <template #title>
          <div class="flex items-center justify-between">
            <div class="text-xl font-bold mr-auto">{{ currentOrganization?.name }}</div>
            <Button
              label="Edit company"
              icon="pi pi-pencil"
              severity="secondary"
              class="p-button-text p-button-sm"
              @click="openOrganizationDialog"
            />
          </div>
        </template>
        <template #subtitle>
          <div class="flex flex-wrap gap-4 text-sm">
            <div><strong>Stage:</strong> {{ engagementStageDisplay }}</div>
            <div>
              <strong>Last Contact:</strong>
              {{
                currentOrganization?.lastContactedAt
                  ? formatDate(currentOrganization.lastContactedAt)
                  : 'N/A'
              }}
            </div>
            <div><strong>Transactions:</strong> {{ transactionCount }}</div>
          </div>
        </template>
        <template #content>
          <div class="space-y-2">
            <div v-if="currentOrganization?.employees" class="text-sm text-gray-600">
              Employees: {{ currentOrganization?.employees }}
            </div>
            <div v-if="currentOrganization?.sales" class="text-sm text-gray-600">
              Sales: {{ currentOrganization?.sales }}
            </div>
            <div v-if="formattedAddress" class="text-sm text-gray-600">
              {{ formattedAddress }}
            </div>
            <div v-if="currentOrganization?.phone" class="text-sm text-gray-600">
              {{ formattedPhone }}
            </div>
            <div v-if="currentOrganization?.email" class="text-sm text-gray-600">
              {{ currentOrganization?.email }}
            </div>
            <div v-if="currentOrganization?.website" class="text-sm text-gray-600">
              {{ currentOrganization?.website }}
            </div>
            <div v-if="currentOrganization?.notes" class="text-sm text-gray-600">
              {{ currentOrganization?.notes }}
            </div>
            <div class="mt-1 flex flex-wrap gap-1">
              <Badge
                v-for="tag in currentOrganization?.tags ?? []"
                :key="tag"
                :value="tag"
                severity="secondary"
              />
            </div>
          </div>
        </template>
        <template #footer>
          <div class="space-y-3 mt-3">
            <div class="flex flex-wrap mt-1">
              <Button
                v-if="currentOrganization?.phone"
                severity="success"
                class="p-button-sm"
                :disabled="!currentOrganization?.phone"
              >
                <a
                  :href="phoneService.getCallUrl(currentOrganization.phone)"
                  @click="handleBigButtonClick"
                  class="flex items-center gap-2 text-white no-underline"
                >
                  <i class="pi pi-phone"></i>
                  Call
                </a>
              </Button>
              <Button
                label="Email"
                icon="pi pi-envelope"
                severity="info"
                @click="emailOrganization"
                :disabled="!currentOrganization?.email"
                class="p-button-sm"
              />
              <Button
                label="Comment"
                icon="pi pi-plus"
                severity="secondary"
                @click="logNote"
                class="p-button-sm"
              />
              <Button
                label="Schedule"
                icon="pi pi-calendar"
                severity="help"
                @click="scheduleFollowup"
                class="p-button-sm"
              />
            </div>
          </div>
        </template>
      </Card>

      <div ref="cardRef" class="w-full">
        <TabView class="mx-auto" v-model:activeIndex="activeIndex">
          <TabPanel :header="`Interactions (${timelineEvents.length})`" :value="0">
            <div class="p-4">
              <CrmTimeline :events="timelineEvents" />
            </div>
          </TabPanel>
          <TabPanel :header="`Contacts (${contactsCount})`" :value="1">
            <Card class="h-full" style="display: flex; flex-direction: column">
              <template #content>
                <div class="employee-list flex-1 overflow-y-auto">
                  <div
                    v-for="person in people"
                    :key="person.id"
                    class="employee-item p-3 border-bottom-1 surface-border"
                  >
                    <div class="flex justify-content-between align-items-center">
                      <div class="cursor-pointer" @click="editPerson(person)">
                        <div class="font-medium text-lg">
                          {{ person.firstName }} {{ person.lastName }}
                        </div>
                        <div class="text-sm text-500">{{ person.jobTitle || 'No title' }}</div>
                        <div v-if="person.contactFor" class="text-sm text-500">
                          Contact for: {{ person.contactFor }}
                        </div>
                      </div>
                      <div class="flex flex-column align-items-end">
                        <div v-if="person.email" class="mb-1">
                          <Button
                            icon="pi pi-envelope"
                            class="p-button-text p-button-sm"
                            @click.stop="emailPerson(person)"
                          />
                          {{ person.email }}
                        </div>
                        <div v-if="person.phone">
                          <Button
                            icon="pi pi-phone"
                            class="p-button-text p-button-sm"
                            @click.stop="callPerson(person)"
                          />
                          {{ person.phone }}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-if="!people || people.length === 0" class="p-3 text-center text-500">
                    No contacts found
                  </div>
                </div>
              </template>
              <template #footer>
                <div class="flex justify-content-end">
                  <Button
                    label="Add Contact"
                    icon="pi pi-plus"
                    class="p-button-text"
                    severity="secondary"
                    @click="addEmployee"
                  />
                </div>
              </template>
            </Card>
          </TabPanel>
          <TabPanel :header="`Comments (${commentsCount})`" :value="2">
            <CommentsCard :organizationId="currentOrganization?.id || ''" />
          </TabPanel>
          <TabPanel :header="`Tasks (${tasksCount})`" :value="3">
            <TaskListCard :organizationId="currentOrganization?.id || ''" />
          </TabPanel>
          <TabPanel :header="`Transactions (${transactionsCount})`" :value="4">
            <TransactionLog :organization-id="currentOrganization?.id || ''" />
          </TabPanel>
          <TabPanel :header="`Emails (${emailsCount})`" :value="5">
            <EmailLog :organization-id="currentOrganization?.id || ''" />
          </TabPanel>
          <TabPanel :header="`Calls (${callsCount})`" :value="6">
            <CallLog :organization-id="currentOrganization?.id || ''" />
          </TabPanel>
        </TabView>
      </div>

      <div class="flex gap-2 mt-4 mb-2">
        <div
          v-for="(_, index) in sortedOrganizations"
          :key="index"
          class="w-2 h-2 rounded-full"
          :class="index === currentIndex ? 'bg-primary-500' : 'bg-gray-300'"
        ></div>
      </div>
    </div>

    <!-- Dialogs -->
    <QuickEditPerson
      v-model:visible="personDialogVisible"
      :person="selectedPerson"
      @saved="onPersonSaved"
    />

    <Dialog
      v-model:visible="emailDialogVisible"
      header="Send Email"
      :style="{ width: '80%', maxWidth: '800px' }"
      :modal="true"
    >
      <EmailComposer
        :organizationId="currentOrganization?.id"
        :personId="selectedPerson?.id"
        @sent="handleEmailSent"
        @error="handleEmailError"
        @cancel="emailDialogVisible = false"
      />
    </Dialog>

    <OrganizationDialog
      v-model:visible="organizationDialogVisible"
      :organization-data="currentOrganization || {}"
      :is-editing="true"
      @organization-saved="handleOrganizationSaved"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useOrganizationStore } from '@/stores/v2/organization'
import { useSwipeNavigation } from '@/composables/useSwipeNavigation'
import { usePersonStore } from '@/stores/v2/person'
import { useCallLogger } from '@/composables/useCallLogger'
import { usePhoneService } from '@/services/phoneService'
import { formatDate, capitalize } from '@/utils/formatters'
import { createPerson } from '@/factories/personFactory'
import {
  getLegacyTimelineForOrganization,
  getTimelineForOrganization,
} from '@/services/crm/crmTimelineService'
import type { TimelineEvent } from '@/components/crm/CrmTimeline.vue'
import type { Person } from '@/types/Person'
import type { Organization } from '@/types/Organization'
import { useCommentStore } from '@/stores/commentStore'
import { useTaskStore } from '@/stores/taskStore'

import TabView from 'primevue/tabview'
import TabPanel from 'primevue/tabpanel'
import Card from 'primevue/card'
import Button from 'primevue/button'
import Badge from 'primevue/badge'
import Dialog from 'primevue/dialog'
import Breadcrumb from 'primevue/breadcrumb'

import CrmTimeline from '@/components/crm/CrmTimeline.vue'
import TransactionLog from '@/components/crm/TransactionLog.vue'
import EmailLog from '@/components/crm/EmailsTab.vue'
import CallLog from '@/components/crm/CallsTab.vue'
import QuickEditPerson from '@/components/crm/QuickEditPerson.vue'
import OrganizationDialog from '@/components/organization/OrganizationDialog.vue'
import EmailComposer from '@/components/email/EmailComposer.vue'
import CommentsCard from '@/components/common/CommentsCard.vue'
import TaskListCard from '@/components/task/TaskListCard.vue'

const route = useRoute()
const router = useRouter()
const organizationStore = useOrganizationStore()
const phoneService = usePhoneService()
const personStore = usePersonStore()
const commentStore = useCommentStore()
const taskStore = useTaskStore()
const { openLogger } = useCallLogger()

const activeIndex = ref(0)
const loading = ref(true)
const personDialogVisible = ref(false)
const selectedPerson = ref<Person | null>(null)
const emailDialogVisible = ref(false)
const organizationDialogVisible = ref(false)
const transactionCount = ref<number | null>(null)
const timelineEvents = ref<TimelineEvent[]>([])

// Create refs for the swipe navigation properties
const swipeTarget = ref<HTMLDivElement | null>(null)
const cardRef = ref<HTMLDivElement | null>(null)

// V2 architecture - local state management
const currentOrganization = ref<Organization | null>(null)
const sortedOrganizations = ref<Organization[]>([])
const currentIndex = ref(0)
const people = ref<Person[]>([])

// Load people for current organization
async function loadPeopleForOrganization(orgId: string) {
  try {
    const search = await personStore.search()
    people.value = await search.byOrganization(orgId).run()
  } catch (error) {
    console.error('Error loading people for organization:', error)
    people.value = []
  }
}

// Computed properties
const formattedAddress = computed(() => {
  if (!currentOrganization.value) return null
  const { address, city, state, zipCode, country } = currentOrganization.value
  const parts = [address, city, state, zipCode, country].filter(Boolean)
  return parts.length > 0 ? parts.join(', ') : null
})

const formattedPhone = computed(() => currentOrganization.value?.phone || null)

const engagementStageDisplay = computed(() =>
  capitalize(currentOrganization.value?.engagementStage ?? 'N/A'),
)

// Count computed properties
const contactsCount = computed(() => people.value?.length || 0)
const commentsCount = computed(
  () =>
    commentStore.items.filter((comment) => comment.organizationId === currentOrganization.value?.id)
      .length,
)
const tasksCount = computed(
  () =>
    taskStore.items.filter((task) => task.organizationId === currentOrganization.value?.id).length,
)
const transactionsCount = computed(() => 0) // TODO: Implement transactions count
const emailsCount = computed(() => 0) // TODO: Implement emails count
const callsCount = computed(() => 0) // TODO: Implement calls count

// Initialize swipe navigation when refs are ready
let swipeNavigationInitialized = false
let swipeNavigationInstance: ReturnType<typeof useSwipeNavigation> | null = null

watch(
  [swipeTarget, cardRef],
  ([newSwipeTarget, newCardRef]) => {
    if (newSwipeTarget && newCardRef && !swipeNavigationInitialized) {
      swipeNavigationInitialized = true

      swipeNavigationInstance = useSwipeNavigation({
        swipeTarget,
        cardRef,
        onNext: () => {
          if (currentIndex.value < sortedOrganizations.value.length - 1) {
            currentIndex.value++
            const nextOrg = sortedOrganizations.value[currentIndex.value]
            if (nextOrg) {
              currentOrganization.value = nextOrg
              router.push(`/crm/organizations/${nextOrg.id}`)
            }
          }
        },
        onPrevious: () => {
          if (currentIndex.value > 0) {
            currentIndex.value--
            const prevOrg = sortedOrganizations.value[currentIndex.value]
            if (prevOrg) {
              currentOrganization.value = prevOrg
              router.push(`/crm/organizations/${prevOrg.id}`)
            }
          }
        },
      })

      swipeNavigationInstance.setup()

      onUnmounted(() => {
        if (swipeNavigationInstance) {
          swipeNavigationInstance.cleanup()
          swipeNavigationInstance = null
        }
        swipeNavigationInitialized = false
      })
    }
  },
  { immediate: true },
)

// Load organizations list for navigation
async function loadOrganizationsList() {
  try {
    const search = await organizationStore.search()
    sortedOrganizations.value = await search.recentlyUpdated(50).run()
  } catch (error) {
    console.error('Error loading organizations list:', error)
  }
}

// Initialize when component is mounted
onMounted(async () => {
  loading.value = true
  try {
    await loadOrganizationsList()
    await taskStore.load()

    if (route.params.id) {
      const orgId = route.params.id as string
      currentOrganization.value = await organizationStore.get(orgId)

      if (currentOrganization.value) {
        // Find the index of current organization in the list
        const index = sortedOrganizations.value.findIndex((org) => org.id === orgId)
        if (index !== -1) {
          currentIndex.value = index
        }

        // Load people and timeline events
        await loadPeopleForOrganization(orgId)
        await loadTimelineEvents(orgId)
      }
    }
  } catch (error) {
    console.error('Error loading organization:', error)
  } finally {
    loading.value = false
  }
})

// Watch for route changes to update current organization
watch(
  () => route.params.id,
  async (newId) => {
    if (newId) {
      const orgId = newId as string
      currentOrganization.value = await organizationStore.get(orgId)

      if (currentOrganization.value) {
        // Find the index of current organization in the list
        const index = sortedOrganizations.value.findIndex((org) => org.id === orgId)
        if (index !== -1) {
          currentIndex.value = index
        }

        // Load people and timeline events
        await loadPeopleForOrganization(orgId)
        await loadTimelineEvents(orgId)
      }
    }
  },
)

// Watch for organization changes to update timeline
watch(
  () => currentOrganization.value?.id,
  async (newId) => {
    if (newId) {
      await loadTimelineEvents(newId)
    }
  },
)

// Function to load timeline events
async function loadTimelineEvents(organizationId: string) {
  try {
    timelineEvents.value = await getLegacyTimelineForOrganization(organizationId)
  } catch (error) {
    console.error('Error loading timeline events:', error)
    timelineEvents.value = []
  }
}

// Event handlers
function callPerson(person: Person) {
  if (person.phone) {
    const callUrl = phoneService.getCallUrl(person.phone)

    // Create a temporary link element for better mobile compatibility
    const link = document.createElement('a')
    link.href = callUrl
    link.target = '_self'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }
}

function emailPerson(person: Person) {
  if (person.email) {
    selectedPerson.value = person
    emailDialogVisible.value = true
  }
}

function editPerson(person: Person) {
  selectedPerson.value = person
  personDialogVisible.value = true
}

function addEmployee() {
  const { id: _id, ...newPerson } = createPerson({
    organizationId: currentOrganization.value?.id,
  })
  selectedPerson.value = newPerson as Person
  personDialogVisible.value = true
}

async function onPersonSaved(person: Person) {
  try {
    if (person.id) {
      await personStore.update(person.id, person)
    } else {
      await personStore.create(person)
    }
    // Reload people for current organization
    if (currentOrganization.value?.id) {
      await loadPeopleForOrganization(currentOrganization.value.id)
    }
  } catch (error) {
    console.error('Error saving person:', error)
  } finally {
    personDialogVisible.value = false
  }
}

// Handle big button click - allows the anchor to work but also opens the call logger
async function handleBigButtonClick() {
  if (!currentOrganization.value?.id) return

  const orgId = currentOrganization.value.id

  // Update last contact
  await organizationStore.update(orgId, {
    lastContactedAt: new Date(),
    updatedAt: new Date(),
  })

  // Add a delay before opening the call logger to allow the phone call prompt to appear first
  setTimeout(() => {
    // Open call logger and refresh timeline after call is logged
    if (currentOrganization.value?.id) {
      openLogger({
        organizationId: currentOrganization.value.id,
        onSubmitted: async () => {
          if (currentOrganization.value?.id) {
            timelineEvents.value = await getTimelineForOrganization(currentOrganization.value.id)
          }
        },
      })
    }
  }, 1000) // 1 second delay
}

function emailOrganization() {
  if (currentOrganization.value?.email) {
    selectedPerson.value = null
    emailDialogVisible.value = true
  }
}

async function handleEmailSent(emailLogId: string) {
  console.log('Email sent successfully, log ID:', emailLogId)
  emailDialogVisible.value = false

  // Update last contact and refresh timeline
  if (currentOrganization.value?.id) {
    const orgId = currentOrganization.value.id
    await organizationStore.update(orgId, {
      lastContactedAt: new Date(),
      updatedAt: new Date(),
    })
    timelineEvents.value = await getTimelineForOrganization(orgId)
  }
}

function handleEmailError(error: Error) {
  console.error('Email sending failed:', error)
}

function logNote() {
  // This would open a dialog to add a comment
  // For now we'll just focus the comment input
  const commentSection = document.querySelector('.comments-card .new-comment-input')
  if (commentSection) {
    ;(commentSection as HTMLElement).focus()
  }
}

function scheduleFollowup() {
  // Would open calendar modal or create follow-up task
}

function openOrganizationDialog() {
  organizationDialogVisible.value = true
}

async function handleOrganizationSaved(updatedOrganization: Organization) {
  if (currentOrganization.value?.id) {
    await organizationStore.update(currentOrganization.value.id, updatedOrganization)
    organizationDialogVisible.value = false
  }
}

const home = { icon: 'pi pi-home', route: '/' }
const breadcrumbItems = computed(() => [
  { label: 'Organizations', route: '/crm/organizations' },
  { label: currentOrganization.value?.name || 'Organization Details' },
])
</script>

<style scoped>
.text-sm strong {
  font-weight: 600;
  margin-right: 0.25rem;
}
.p-card {
  margin-bottom: 1rem;
}
.employee-list {
  max-height: 400px;
  overflow-y: auto;
}
.employee-item {
  transition: background-color 0.2s;
}
.employee-item:hover {
  background-color: var(--surface-100);
}
.employee-item:last-child {
  border-bottom: none !important;
}
.cursor-pointer {
  cursor: pointer;
}
.field {
  margin-bottom: 1rem;
}
.p-tabview-panels {
  padding: 0 !important;
}
</style>
